import type { DOMEvent } from '../types/html-event-target'
import type { Nullable } from '../types/nullable'

/**
 * Frozen array of all element Ids that can close a pop up.
 */
const closeTargets: readonly string[] = Object.freeze([
  '#closeAvoidDeliveryDelays',
  '#closeDiscountDetails',
  '#closeBestPricesOnline',
  '#closeCustomerService',
  '#closeExpressDelivery',
  '#closeFreeDelivery',
  '#closeGoogleReviews',
  '#closeSelectDeliveryDate',
  '#closeUnlimitedFreeCutting'
])

/**
 * Function to add pop up functionality to the site.
 * Used on documentReady
 * @see /app/views/shared/pop_ups/
 */
export default (): void => {
  let previousElement: HTMLElement | null
  /**
   * Closes all pop ups.
   * Slow but ensures only one pop up can be open at any one time.
   */
  function closeAllPopUps (): void {
    const popUps: HTMLElement[] = Array.from(document.querySelectorAll('.nav-pop-up'))

    popUps.forEach((element) => { element.style.display = 'none' })
    if (previousElement) {
      previousElement.focus()
    }
  }

  /**
   * Makes a backend request to the PopUpsController to dynamically fetch content
   * Useful for hiding repetetive content from search engines
   * @param partialName The rails partial name of the pop up
   */
  async function fetchPopUp (partialName: string): Promise<string> {
    return fetch(`/pop_ups/${partialName}`).then((response) => (
      response.text()
    ))
  }

  /**
   * Checks whether a given element should open a pop up onclick
   * @param element HTMLElement
   */
  const opensPopUp = (element: HTMLElement): boolean => (element.hasAttribute('data-open'))

  document.addEventListener('click', (event: DOMEvent<HTMLElement>): void => {
    const popUps: HTMLElement = document.querySelector('.pop-ups')
    const { target } = event

    /**
     * Frozen array of Target elements.
     * This is for the link to the pop up discount link in page content as well as the discount nav.
     */
    const targetEls: readonly HTMLElement[] = Object.freeze([
      target,
      target.parentElement
    ])

    /**
     * Finds a pop up element (if it exists) from the openMap Map
     * @returns HTMLElement?
     */
    function findPopUp (): Nullable<HTMLElement> {
      let partialName: string | undefined

      targetEls.forEach((el): void => {
        if (el !== null) {
          if (opensPopUp(el)) {
            partialName = el.dataset.open
          }
        }
      })

      if (typeof partialName === 'string' && popUps) {
        return popUps.querySelector(`[data-identifer='${partialName}']`) as HTMLElement
      }

      return null
    }

    if (!findPopUp()) return

    const popUp = findPopUp() as HTMLElement

    targetEls.forEach(async (el: HTMLElement) => {
      previousElement = document.activeElement
      if (opensPopUp(el)) {
        closeAllPopUps()
        const fetched = await fetchPopUp(el.dataset.open)

        popUp.innerHTML = fetched
        const firstChild = popUp.children[0] as HTMLElement
        firstChild.style.display = 'block'
        const popUpClose = firstChild.querySelector('.close-pop-up') as HTMLElement
        popUpClose.focus()
      }
    })
  })

  closeTargets.forEach((): void => {
    document.addEventListener('click', (event: DOMEvent<HTMLElement>): void => {
      const closeEls: HTMLElement[] = closeTargets.map((target) => document.querySelector(target))

      if (closeEls.includes(event.target)) {
        closeAllPopUps()
      }
    })
  })
}
